import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCog, Plus, X, ChevronDown, Edit3, Trash2 } from "lucide-react";
import { useTechs } from "@/context/TechContext";
import { Header } from "@/components/Header";
import { TechFormModal } from "@/components/TechFormModal";

const FieldTechsPage = () => {
  const navigate = useNavigate();
  const { techs, addTech, updateTech, deleteTech } = useTechs();
  const [showModal, setShowModal] = useState(false);
  const [selectedTech, setSelectedTech] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [techToDelete, setTechToDelete] = useState(null);
  const [sortBy, setSortBy] = useState('lastName');

  const sortedTechs = [...techs].sort((a, b) => {
    if (sortBy === 'lastName') return a.lastName.localeCompare(b.lastName);
    if (sortBy === 'area') return (a.area || '').localeCompare(b.area || '');
    return 0;
  });

  const handleAddClick = () => {
    setSelectedTech(null);
    setShowModal(true);
  };

  const handleEditClick = (tech) => {
    setSelectedTech(tech);
    setShowModal(true);
  };

  const handleDeleteClick = (tech) => {
    setTechToDelete(tech);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = () => {
    if (techToDelete) {
      deleteTech(techToDelete.id);
    }
    setShowDeleteConfirm(false);
    setTechToDelete(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTech(null);
  };

  const handleSubmit = (formData) => {
    if (selectedTech) {
      updateTech(selectedTech.id, formData);
    } else {
      addTech(formData);
    }
    handleCloseModal();
  };

  return (
    <div className="app-container" data-testid="field-techs-page">
      <Header />

      {/* Field Techs Content */}
      <main className="main-content">
        <div className="device-page-header">
          <div>
            <h1 className="device-page-title tech-title">
              <UserCog size={24} className="tech-icon" />
              Field Techs
            </h1>
            <p className="device-page-subtitle">{techs.length} technician{techs.length !== 1 ? 's' : ''} registered</p>
          </div>
          <div className="header-actions">
            <button className="btn-add-tech" onClick={handleAddClick} data-testid="add-tech-btn">
              <Plus size={16} />
              Add Tech
            </button>
            <button className="close-btn" onClick={() => navigate('/settings')} data-testid="close-btn">
              <X size={24} />
            </button>
          </div>
        </div>

        {techs.length === 0 ? (
          <div className="empty-state" data-testid="empty-state">
            <UserCog size={48} className="empty-state-icon" />
            <h3>No Field Techs</h3>
            <p>Add your first field technician to get started.</p>
            <button className="btn-back" onClick={handleAddClick}>
              Add Field Tech
            </button>
          </div>
        ) : (
          <div className="techs-sort-bar" data-testid="techs-sort-bar">
            <label className="sort-label">
              <ChevronDown size={14} />
              SORT BY:
            </label>
            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)} 
              className="sort-select"
              data-testid="techs-sort-select"
            >
              <option value="lastName">Last Name</option>
              <option value="area">Area</option>
            </select>
          </div>
        )}

        {techs.length > 0 && (
          <div className="techs-list" data-testid="techs-list">
            {sortedTechs.map(tech => (
              <div key={tech.id} className="tech-card" data-testid={`tech-card-${tech.id}`}>
                <div className="tech-card-header">
                  <div className="tech-name-section">
                    <UserCog size={20} className="tech-card-icon" />
                    <span className="tech-name">{tech.firstName} {tech.lastName}</span>
                    <span className="tech-company">{tech.company}</span>
                  </div>
                  <div className="tech-actions">
                    <button 
                      className="tech-action-btn edit" 
                      onClick={() => handleEditClick(tech)}
                      data-testid={`edit-tech-btn-${tech.id}`}
                    >
                      <Edit3 size={14} />
                      EDIT
                    </button>
                    <button 
                      className="tech-action-btn delete" 
                      onClick={() => handleDeleteClick(tech)}
                      data-testid={`delete-tech-btn-${tech.id}`}
                    >
                      <Trash2 size={14} />
                      DELETE
                    </button>
                  </div>
                </div>
                <div className="tech-card-body">
                  <div className="tech-info-row">
                    <span className="tech-label">Email:</span>
                    <span className="tech-value">{tech.email}</span>
                    <span className="tech-label">Mobile:</span>
                    <span className="tech-value">{tech.mobile}</span>
                  </div>
                  <div className="tech-info-row">
                    <span className="tech-label">Address:</span>
                    <span className="tech-value">
                      {tech.address && `${tech.address}, `}{tech.city}, {tech.state} {tech.zip}
                    </span>
                  </div>
                  <div className="tech-info-row">
                    <span className="tech-label">Area:</span>
                    <span className="tech-value area">{tech.area || 'Not assigned'}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Tech Form Modal */}
      {showModal && (
        <TechFormModal 
          tech={selectedTech}
          onClose={handleCloseModal}
          onSubmit={handleSubmit}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && techToDelete && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content modal-small" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title delete-title">
                <Trash2 size={20} />
                Delete Tech
              </h2>
              <button className="modal-close-btn" onClick={() => setShowDeleteConfirm(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="delete-confirm-body">
              <p>Are you sure you want to delete <strong>{techToDelete.firstName} {techToDelete.lastName}</strong>?</p>
              <p className="delete-warning">This action cannot be undone.</p>
            </div>
            <div className="form-actions">
              <button className="btn-cancel" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="btn-submit btn-delete" onClick={handleConfirmDelete} data-testid="confirm-delete-btn">
                <Trash2 size={16} />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FieldTechsPage;
